import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import type { LaudoWithDetails } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { generateAndUploadLaudoPdf } from "@/lib/laudo-pdf";
import { useToast } from "@/hooks/use-toast";

interface FinalizarLaudoProps {
  laudo: LaudoWithDetails;
  onFinalize: (pdfUrl: string) => void;
  isLoading?: boolean;
}

export function FinalizarLaudo({
  laudo,
  onFinalize,
  isLoading = false,
}: FinalizarLaudoProps) {
  const [open, setOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [etapaPdf, setEtapaPdf] = useState("Gerando PDF…");
  const { session } = useAuth();
  const { toast } = useToast();

  const allDestinacoes =
    !!laudo.laudoPecas &&
    laudo.laudoPecas.length > 0 &&
    laudo.laudoPecas.every((p) => !!p.destinacao);

  const isSigned = laudo.status === "assinado";

  const canFinalize = () => isSigned && allDestinacoes;

  const getMissingItems = () => {
    const missing: string[] = [];
    if (!isSigned) missing.push("Laudo deve estar assinado antes de finalizar");
    if (!allDestinacoes) {
      const sem = laudo.laudoPecas?.filter((p) => !p.destinacao).length ?? 0;
      if (sem > 0) missing.push(`Destinação de ${sem} peça(s) pendente(s)`);
    }
    return missing;
  };

  const handleFinalize = async () => {
    setShowConfirm(false);
    setIsGenerating(true);

    try {
      const accessToken = session?.access_token ?? "";
      const pdfUrl = await generateAndUploadLaudoPdf(laudo, accessToken, { onProgress: setEtapaPdf });
      onFinalize(pdfUrl);
      setOpen(false);
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      toast({
        variant: "destructive",
        title: "Erro ao gerar PDF",
        description:
          err instanceof Error ? err.message : "Tente novamente.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const busy = isLoading || isGenerating;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={laudo.status === "finalizado"}
          >
            Finalizar Laudo
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Finalizar Laudo
            </DialogTitle>
            <DialogDescription>
              Revise as informações antes de finalizar
            </DialogDescription>
          </DialogHeader>

          <Card className="bg-slate-50">
            <CardHeader>
              <CardTitle className="text-base">
                Checklist de Finalização
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Assinatura */}
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    isSigned ? "bg-green-500" : "bg-slate-300"
                  }`}
                >
                  {isSigned ? "✓" : "○"}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Laudo assinado</p>
                  <p className="text-sm text-slate-600">
                    {isSigned
                      ? laudo.dataAssinatura
                        ? "Assinado em " +
                          new Date(laudo.dataAssinatura).toLocaleDateString("pt-BR")
                        : "Laudo assinado"
                      : "Requer assinatura do responsável técnico antes de finalizar"}
                  </p>
                </div>
              </div>

              {/* Destinações */}
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    allDestinacoes ? "bg-green-500" : "bg-slate-300"
                  }`}
                >
                  {allDestinacoes ? "✓" : "○"}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    Todas as peças com destinação
                  </p>
                  <p className="text-sm text-slate-600">
                    {laudo.laudoPecas?.filter((p) => !!p.destinacao).length || 0}{" "}
                    de {laudo.laudoPecas?.length || 0} peça(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {!canFinalize() && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Não é possível finalizar:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {getMissingItems().map((item, index) => (
                    <li key={index} className="text-sm">
                      {item}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {canFinalize() && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                O laudo está pronto para ser finalizado. O PDF será gerado
                automaticamente.
              </AlertDescription>
            </Alert>
          )}

          {isGenerating && <p role="status" aria-live="polite" className="text-sm text-muted-foreground">{etapaPdf}</p>}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            {canFinalize() && (
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={busy || !canFinalize()}
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gerando PDF…
                  </>
                ) : (
                  "Finalizar Laudo"
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Finalização</AlertDialogTitle>
            <AlertDialogDescription>
              Após finalizar o laudo, você não poderá mais fazer alterações.
              O PDF será gerado e salvo automaticamente. Esta ação é
              irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-3">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalize}
              className="bg-red-600 hover:bg-red-700"
            >
              Finalizar
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
